import * as React from "react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";

interface ResponsiveDialogProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "className"> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  className?: string;
}

/**
 * Responsive wrapper that renders a Drawer on mobile (<768px) and a Dialog on desktop.
 * Use the sub-components (ResponsiveDialogHeader, etc.) for consistent API.
 *
 * TES-2288: `className` wird auf BEIDE Varianten angewandt. Desktop-only-Geometrie
 * (Breiten-Caps, feste Höhen) MUSS `md:`-präfixt sein (768px = MOBILE_BREAKPOINT) —
 * `sm:` feuert schon ab 640px und trifft auf 640–767px-Viewports den Bottom-Drawer
 * (Guard: responsive-dialog-breakpoint-guard.test.ts).
 *
 * TES-2034: extra HTML attributes (e.g. `data-testid`) are forwarded onto the underlying
 * DrawerContent/DialogContent — callers that used to set a testid directly on `DialogContent`
 * keep the same query surface after migrating to ResponsiveDialog.
 */
function ResponsiveDialog({
  open,
  onOpenChange,
  children,
  className,
  ...rest
}: ResponsiveDialogProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent
          className={cn("max-h-[90dvh] flex flex-col overflow-hidden", className)}
          {...rest}
        >
          {children}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn("sm:max-w-[500px] max-h-[90dvh] flex flex-col overflow-hidden", className)}
        {...rest}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
}

function ResponsiveDialogHeader({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const isMobile = useIsMobile();
  return isMobile ? (
    <DrawerHeader className={className}>{children}</DrawerHeader>
  ) : (
    <DialogHeader className={className}>{children}</DialogHeader>
  );
}

function ResponsiveDialogTitle({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const isMobile = useIsMobile();
  return isMobile ? (
    <DrawerTitle className={className}>{children}</DrawerTitle>
  ) : (
    <DialogTitle className={className}>{children}</DialogTitle>
  );
}

function ResponsiveDialogDescription({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const isMobile = useIsMobile();
  return isMobile ? (
    <DrawerDescription className={className}>{children}</DrawerDescription>
  ) : (
    <DialogDescription className={className}>{children}</DialogDescription>
  );
}

function ResponsiveDialogFooter({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const isMobile = useIsMobile();
  return isMobile ? (
    <DrawerFooter className={className}>{children}</DrawerFooter>
  ) : (
    <DialogFooter className={className}>{children}</DialogFooter>
  );
}

function ResponsiveDialogClose({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  const isMobile = useIsMobile();
  return isMobile ? (
    <DrawerClose className={className}>{children}</DrawerClose>
  ) : (
    <DialogClose className={className}>{children}</DialogClose>
  );
}

export {
  ResponsiveDialog,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogClose,
};
